import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { corValida, COR_TRIBO_PADRAO } from '@/lib/tribo';
import Card from '@/components/ui/Card';

interface TriboBarra {
  id: number;
  nome: string;
  pessoas: number;
  pct: number;
  cor: string | null;
}

interface DadosTribos {
  equipe_com_conta: number;
  com_tribo: number;
  nao_campistas: number;
  sem_resposta: number;
  tribos: TriboBarra[];
}

function numero(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function normalizar(v: unknown): DadosTribos {
  const d = (v ?? {}) as Record<string, unknown>;
  const tribosBrutas = Array.isArray(d.tribos) ? d.tribos : [];
  const tribos = tribosBrutas
    .map((t) => {
      const o = (t ?? {}) as Record<string, unknown>;
      const id = Number(o.id);
      if (!Number.isFinite(id)) return null;
      return {
        id,
        nome: String(o.nome ?? ''),
        pessoas: numero(o.pessoas),
        pct: numero(o.pct),
        cor: corValida(typeof o.cor === 'string' ? o.cor : null),
      };
    })
    .filter((t): t is TriboBarra => t !== null)
    .sort((a, b) => b.pct - a.pct);

  return {
    equipe_com_conta: numero(d.equipe_com_conta),
    com_tribo: numero(d.com_tribo),
    nao_campistas: numero(d.nao_campistas),
    sem_resposta: numero(d.sem_resposta),
    tribos,
  };
}

// ---------------------------------------------------------------------------
// Card "Tribos da equipe" (painel do organizador): de onde vieram os campistas
// que estão servindo. Lê tribos_do_evento(evento). Só leitura.
// ---------------------------------------------------------------------------
export default function TribosDaEquipe({ eventoId }: { eventoId: number }) {
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [dados, setDados] = useState<DadosTribos | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const { data, error } = await supabase.rpc('tribos_do_evento', { p_evento: eventoId });
      if (error) throw error;
      setDados(normalizar(data));
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não foi possível carregar as tribos.'));
    } finally {
      setCarregando(false);
    }
  }, [eventoId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (carregando) {
    return <div className="h-32 w-full animate-pulse rounded-[18px] bg-background-200/70" />;
  }

  if (erro) {
    return (
      <Card className="p-5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[14px] text-foreground-600">{erro}</p>
          <button
            type="button"
            onClick={() => void carregar()}
            className="aperta shrink-0 cursor-pointer whitespace-nowrap rounded-full bg-background-100 px-3 py-1.5 text-[13px] font-semibold text-foreground-700"
          >
            Tentar de novo
          </button>
        </div>
      </Card>
    );
  }

  if (!dados) return null;

  const respondidas = dados.com_tribo + dados.nao_campistas;
  const total = dados.equipe_com_conta;

  // Evento sem equipe (nada a mostrar): esconde o card inteiro.
  if (total === 0 && respondidas === 0 && dados.tribos.length === 0) return null;

  return (
    <Card className="mb-4 p-5">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-accent-100 text-[22px] text-accent-700">
          <i className="ri-tent-line" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-[16px] font-extrabold leading-tight text-foreground-950">
            Tribos da equipe
          </h2>
          <p className="mt-0.5 text-[13px] text-foreground-600">
            De onde vieram os campistas que estão servindo.
          </p>
        </div>
      </div>

      {dados.tribos.length === 0 && respondidas === 0 ? (
        <p className="mt-4 text-[14px] text-foreground-600">
          Ninguém respondeu ainda — a pergunta aparece no app de quem é da equipe.
        </p>
      ) : (
        <>
          {dados.tribos.length > 0 && (
            <div className="mt-4 space-y-3">
              {dados.tribos.map((t) => (
                <div key={t.id}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="min-w-0 truncate text-[14px] font-semibold text-foreground-800">
                      {t.nome}
                    </span>
                    <span className="shrink-0 whitespace-nowrap text-[13px] text-foreground-500">
                      {Math.round(t.pct)}% · {t.pessoas}{' '}
                      {t.pessoas === 1 ? 'pessoa' : 'pessoas'}
                    </span>
                  </div>
                  <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-background-200">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.max(0, Math.min(100, t.pct))}%`,
                        backgroundColor: t.cor ?? COR_TRIBO_PADRAO,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          <p className="mt-4 text-[13px] text-foreground-500">
            {respondidas} de {total} responderam · {dados.nao_campistas}{' '}
            {dados.nao_campistas === 1 ? 'não foi campista' : 'não foram campistas'}
          </p>
        </>
      )}
    </Card>
  );
}